'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

export default function SmoothScroll({ children }: { children: React.ReactNode }) {
    const pathname = usePathname();
    const lenisRef = useRef<Lenis | null>(null);

    useEffect(() => {
        const lenis = new Lenis({
            duration: 1.2,
            easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            direction: 'vertical',
            gestureDirection: 'vertical',
            smooth: true,
            mouseMultiplier: 1,
            smoothTouch: false,
            touchMultiplier: 1,
        } as any);

        lenisRef.current = lenis;

        lenis.on('scroll', ScrollTrigger.update);

        gsap.ticker.add((time) => {
            lenis.raf(time * 1000);
        });

        gsap.ticker.lagSmoothing(0);

        return () => {
            gsap.ticker.remove(gsap.ticker.lagSmoothing);
            lenis.destroy();
            lenisRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (lenisRef.current) {
            if (window.location.hash) {
                // Determine if we want immediate jump or smooth scroll
                // For 'redirect', immediate is often better to avoid disorientation, 
                // but user asked for 'smooth'. Let's try smooth if it's a deep link.
                // Actually, standard behavior for new page load is immediate jump.
                // However, lenis.scrollTo defaults to immediate: false (smooth) unless specified? 
                // Let's explicitly separate.
                const hash = window.location.hash;
                // Timeout to ensure DOM is ready? 
                // Sometimes Next.js takes a moment to render the target section.
                setTimeout(() => {
                    lenisRef.current?.scrollTo(hash);
                }, 100);
            } else {
                lenisRef.current.scrollTo(0, { immediate: true });
            }
        }
    }, [pathname]);

    return <>{children}</>;
}
